import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useNavigate } from 'react-router-dom';

const FloatingVoiceAssistant = React.memo(({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [response, setResponse] = useState('');
  const [streamingText, setStreamingText] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [navigationSuggestions, setNavigationSuggestions] = useState([]);
  const recognitionRef = useRef(null);
  const streamIntervalRef = useRef(null);
  const handleVoiceQueryRef = useRef(null);
  const navigate = useNavigate();

  const startListening = () => {
    if (recognitionRef.current) {
      setTranscript('');
      setResponse('');
      setStreamingText('');
      setNavigationSuggestions([]);
      setIsListening(true);
      recognitionRef.current.start();
    } else {
      alert('Speech recognition not supported in this browser');
    }
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsListening(false);
  };

  const simulateStreaming = useCallback((text) => {
    setIsStreaming(true);
    setStreamingText('');
    let index = 0;
    
    // Clear any existing interval
    if (streamIntervalRef.current) {
      clearInterval(streamIntervalRef.current);
    }
    
    streamIntervalRef.current = setInterval(() => {
      if (index < text.length) {
        // Add 2-5 characters at a time for natural streaming
        const chunkSize = Math.floor(Math.random() * 4) + 2;
        const nextChunk = text.slice(index, Math.min(index + chunkSize, text.length));
        index += chunkSize;
        
        setStreamingText(prev => prev + nextChunk);
      } else {
        clearInterval(streamIntervalRef.current);
        setIsStreaming(false);
        setResponse(text);
        setStreamingText('');
      }
    }, 30); // 30ms per chunk for smooth streaming
  }, []);

  const handleVoiceQuery = useCallback(async (text) => {
    if (!user || !text.trim()) return;

    setIsProcessing(true);
    setResponse('');
    setStreamingText('');

    try {
      const response = await axios.post(`${API}/voice-assistant`, {
        user_id: user.id,
        message: text
      });

      // Start streaming the response text
      simulateStreaming(response.data.response);
      
      let allSuggestions = [];

      // Handle primary_navigation
      if (response.data.primary_navigation) {
        allSuggestions = [response.data.primary_navigation];
      }

      // Append any additional navigation_suggestions
      if (response.data.navigation_suggestions) {
        allSuggestions = [...allSuggestions, ...response.data.navigation_suggestions];
      }

      const dedupedSuggestions = [];
      const seenSignatures = new Set();

      for (const suggestion of allSuggestions) {
        const signature = `${suggestion.route ?? ''}|${suggestion.target_id ?? ''}|${suggestion.reason ?? ''}`;
        if (seenSignatures.has(signature)) {
          continue;
        }
        seenSignatures.add(signature);
        dedupedSuggestions.push(suggestion);
      }

      setNavigationSuggestions(dedupedSuggestions);

      // Auto-navigate if there's a primary navigation with auto_navigate flag
      if (response.data.primary_navigation && response.data.primary_navigation.auto_navigate) {
        setTimeout(() => {
          const nav = response.data.primary_navigation;
          navigate(nav.route, { 
            state: { 
              highlightId: nav.target_id,
              highlightTitle: nav.reason
            } 
          });
          setIsOpen(false);
        }, 2500); // Increased to 2.5s to account for streaming
      }

      // Speak the response
      if ('speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(response.data.response);
        utterance.rate = 0.9;
        utterance.pitch = 1;
        utterance.volume = 0.9;
        window.speechSynthesis.speak(utterance);
      }
    } catch (error) {
      console.error('Voice assistant error:', error);
      setResponse('Sorry, I encountered an error processing your request.');
    } finally {
      setIsProcessing(false);
    }
  }, [user, navigate, simulateStreaming]);

  useEffect(() => {
    handleVoiceQueryRef.current = handleVoiceQuery;
  }, [handleVoiceQuery]);

  const handleNavigate = (nav) => {
    // Use target_id if available
    navigate(nav.route, { state: { highlightId: nav.target_id, highlightTitle: nav.reason } });
    setIsOpen(false);
  };

  useEffect(() => {
    const hasSpeechRecognition =
      'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;

    if (!hasSpeechRecognition) {
      return undefined;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      const resultTranscript = event.results[0][0].transcript;
      setTranscript(resultTranscript);
      handleVoiceQueryRef.current?.(resultTranscript);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;

    return () => {
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      recognition.stop();
      recognitionRef.current = null;
    };
  }, []);

  if (!user) return null;

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          border: 'none',
          boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '28px',
          color: 'white',
          zIndex: 9999,
          transition: 'transform 0.2s, box-shadow 0.2s'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.1)';
          e.currentTarget.style.boxShadow = '0 6px 30px rgba(102, 126, 234, 0.6)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 4px 20px rgba(102, 126, 234, 0.4)';
        }}
      >
        {isListening ? '🎤' : '🤖'}
      </button>

      {/* Assistant Panel */}
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            bottom: '100px',
            right: '24px',
            width: '520px',
            maxHeight: '720px',
            background: 'white',
            borderRadius: '16px',
            boxShadow: '0 18px 48px rgba(15, 23, 42, 0.22)',
            zIndex: 9998,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
          }}
        >
          {/* Header */}
          <div
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: '22px',
              color: 'white'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700' }}>KATI Assistant</h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '13px', opacity: 0.9 }}>
                  Voice-activated knowledge copilot
                </p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '32px',
                  height: '32px'
                }}
              >
                ×
              </button>
            </div>
          </div>

          {/* Content */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '22px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}
          >
            {/* Voice Control */}
            <div style={{ textAlign: 'center' }}>
              <button
                onClick={isListening ? stopListening : startListening}
                disabled={isProcessing}
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  border: 'none',
                  background: isListening ? '#ef4444' : '#667eea',
                  color: 'white',
                  fontSize: '32px',
                  cursor: isProcessing ? 'not-allowed' : 'pointer',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  transition: 'all 0.2s'
                }}
              >
                {isProcessing ? '⏳' : isListening ? '🛑' : '🎤'}
              </button>
              <p style={{ marginTop: '12px', fontSize: '13px', color: '#64748b' }}>
                {isProcessing ? 'Processing...' : isListening ? 'Listening...' : 'Click to speak'}
              </p>
            </div>

            {/* Transcript */}
            {transcript && (
              <div
                style={{
                  padding: '12px 16px',
                  background: '#f0f9ff',
                  borderRadius: '8px',
                  borderLeft: '4px solid #667eea'
                }}
              >
                <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px', fontWeight: '600' }}>
                  YOU ASKED:
                </div>
                <div style={{ fontSize: '14px', color: '#1e293b' }}>{transcript}</div>
              </div>
            )}

            {/* Response */}
            {(response || streamingText) && (
              <div
                style={{
                  padding: '26px 28px',
                  background: 'linear-gradient(135deg, rgba(243, 244, 255, 0.96) 0%, rgba(224, 231, 255, 0.88) 100%)',
                  borderRadius: '20px',
                  border: '1px solid rgba(99, 102, 241, 0.25)',
                  boxShadow: '0 18px 40px rgba(79, 70, 229, 0.16)',
                  maxHeight: '320px',
                  overflowY: 'auto',
                  marginBottom: '12px'
                }}
              >
                <div style={{ fontSize: '13px', color: '#0f172a', letterSpacing: '0.08em', marginBottom: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px', textTransform: 'uppercase' }}>
                  KATI SAYS:
                  {isStreaming && (
                    <span style={{ 
                      display: 'inline-block', 
                      width: '6px', 
                      height: '6px', 
                      background: '#10b981', 
                      borderRadius: '50%',
                      animation: 'pulse 1s infinite'
                    }}></span>
                  )}
                </div>
                <div style={{ 
                  fontSize: '17px', 
                  color: '#0f172a', 
                  lineHeight: '1.95',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  letterSpacing: '0.012em'
                }}>
                  {streamingText || response}
                  {isStreaming && (
                    <span style={{ 
                      display: 'inline-block', 
                      width: '2px', 
                      height: '20px', 
                      background: '#4338ca', 
                      marginLeft: '4px',
                      animation: 'blink 1s infinite'
                    }}></span>
                  )}
                </div>
              </div>
            )}

            {/* Navigation Suggestions */}
            {navigationSuggestions.length > 0 && (
              <div style={{ marginTop: '12px' }}>
                <div style={{ fontSize: '12px', color: '#475569', marginBottom: '10px', fontWeight: '600', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                  QUICK ACTIONS:
                </div>
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '10px', 
                  background: 'rgba(15, 23, 42, 0.04)', 
                  borderRadius: '12px', 
                  padding: '12px'
                }}>
                  {navigationSuggestions.map((nav, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleNavigate(nav)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '9px 16px',
                        borderRadius: '999px',
                        border: nav.auto_navigate ? '1px solid transparent' : '1px solid rgba(99,102,241,0.35)',
                        background: nav.auto_navigate ? 'linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)' : 'white',
                        color: nav.auto_navigate ? 'white' : '#312e81',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        boxShadow: nav.auto_navigate ? '0 8px 18px rgba(99,102,241,0.28)' : '0 4px 10px rgba(15,23,42,0.08)',
                        transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                        minHeight: '38px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = nav.auto_navigate
                          ? '0 12px 22px rgba(99,102,241,0.32)'
                          : '0 6px 14px rgba(15,23,42,0.12)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = nav.auto_navigate
                          ? '0 8px 18px rgba(99,102,241,0.28)'
                          : '0 4px 10px rgba(15,23,42,0.08)';
                      }}
                    >
                      <span style={{ fontSize: '14px' }}>
                        {nav.route === '/tickets' ? '📋' : 
                        nav.route === '/documents' ? '📄' : 
                        nav.route === '/onboarding' ? '🚀' : 
                        nav.route === '/organization' ? '🏢' : 
                        nav.route === '/memory' ? '🔍' : '📊'}
                      </span>
                      <span>{nav.reason}</span>
                      {nav.auto_navigate && <span style={{ fontSize: '12px', opacity: 0.85 }}>Auto</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Help Text */}
            {!transcript && !response && (
              <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎙️</div>
                <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
                  Ask me anything about:
                  <br />
                  • GitHub CLI issues & PRs
                  <br />
                  • Documents & files
                  <br />
                  • Tickets & tasks
                  <br />
                  • Team & organization
                  <br />
                  <br />
                  <span style={{ fontSize: '13px', fontWeight: '600' }}>
                    I'll answer and take you where you need to go!
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
});

export default FloatingVoiceAssistant;
